/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React from "react";
import { Box, Paper, Typography } from "@mui/material";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import PercentOutlinedIcon from "@mui/icons-material/PercentOutlined";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
  StatisticsTitle,
  StatisticsValue,
  CardContainer,
  IconContainer,
  InformationContainer,
  TitleContainer,
  LightTooltip,
  StyledInfoIcon,
} from "../../../components/statistics_cards/styles";
import { StatisticSkeletalLoader } from "../../../components/statistic_skeletal_loader";
import {
  PERQ_SUMMARY_CLICKABLE_CARDS,
  formatMoney,
  formatPercent,
} from "../../../utils/perqReportDrilldown";
import { ReceiptTooltip, SummaryReceipt, receiptDescription } from "./summary_receipt";

const BORDER_COLOR = "#eee";
const ICON_COLOR = "#f97316";
const NEUTRAL_VALUE = "—";

const SUMMARY_CARDS = [
  {
    key: "orders",
    title: "Orders",
    receiptRow: "gross_sales",
    icon: <ReceiptLongOutlinedIcon sx={{ color: ICON_COLOR }} />,
    getValue: (data) => (data?.orders_count ?? 0).toString(),
    getSubValue: (data) => `${formatMoney(data?.gross_sales)} gross`,
  },
  {
    key: "discount_total",
    title: "Perq Total",
    receiptRow: "discount_total",
    icon: <PercentOutlinedIcon sx={{ color: ICON_COLOR }} />,
    getValue: (data) => formatMoney(data?.discount_total),
    getSubValue: (data) => `${formatPercent(data?.discount_percent)} of gross sales`,
  },
  {
    key: "managed",
    title: "Managed Perqs",
    receiptRow: "managed",
    icon: <VerifiedOutlinedIcon sx={{ color: ICON_COLOR }} />,
    getValue: (data) => formatMoney(data?.managed_total),
  },
  {
    key: "unmanaged",
    title: "Unmanaged Discount",
    receiptRow: "unmanaged",
    icon: <RemoveCircleOutlineIcon sx={{ color: ICON_COLOR }} />,
    getValue: (data) => formatMoney(data?.unmanaged_total),
  },
  {
    key: "unclassified",
    title: "Unclassified Discount",
    receiptRow: "unclassified",
    icon: <HelpOutlineIcon sx={{ color: ICON_COLOR }} />,
    getValue: (data) => formatMoney(data?.unclassified_total),
  },
  {
    key: "managed_without_check_in",
    title: "Managed w/o check-in",
    description: "Orders with a managed perq but no check-in",
    icon: <WarningAmberIcon sx={{ color: ICON_COLOR }} />,
    getValue: (data) => (data?.managed_without_check_in_count ?? 0).toString(),
    getSubValue: (data) => `${data?.low_tip_flagged_count ?? 0} low-tip flagged`,
  },
];

const LAYOUTS = {
  sm: [2, 2, 2],
  lg: [3, 3],
};

const getCardBorders = (index, layout) => {
  let row = 0;
  let offset = 0;

  for (let r = 0; r < layout.length; r++) {
    if (index < offset + layout[r]) {
      row = r;
      break;
    }
    offset += layout[r];
  }

  const positionInRow = index - offset;
  const cardsInRow = layout[row];
  const isFirstRow = row === 0;
  const isLastRow = row === layout.length - 1;
  const isFirstInRow = positionInRow === 0;
  const isLastInRow = positionInRow === cardsInRow - 1;

  return {
    borderTop: isFirstRow ? `1px solid ${BORDER_COLOR}` : "none",
    borderBottom: `1px solid ${BORDER_COLOR}`,
    borderLeft: isFirstInRow ? `1px solid ${BORDER_COLOR}` : "none",
    borderRight: `1px solid ${BORDER_COLOR}`,
    borderTopLeftRadius: isFirstRow && isFirstInRow ? 10 : 0,
    borderTopRightRadius: isFirstRow && isLastInRow ? 10 : 0,
    borderBottomLeftRadius: isLastRow && isFirstInRow ? 10 : 0,
    borderBottomRightRadius: isLastRow && isLastInRow ? 10 : 0,
  };
};

export const PerqReportSummary = ({
  isMobile = false,
  data = {},
  loading = false,
  error = false,
  selectedDrilldown = null,
  onCardClick = () => {},
}) => (
  <Box
    data-testid="perq-summary-container"
    sx={{
      display: "grid",
      margin: "20px 0px",
      borderRadius: "10px",
      gridTemplateColumns: "repeat(2, 1fr)",
      "@media (min-width: 1000px)": {
        gridTemplateColumns: "repeat(3, 1fr)",
      },
    }}
  >
    {SUMMARY_CARDS.map((card, index) => {
      const isClickable = PERQ_SUMMARY_CLICKABLE_CARDS.includes(card.key);
      const isSelected = isClickable && selectedDrilldown === card.key;
      const subValue = card.getSubValue ? card.getSubValue(data) : null;

      const smBorders = getCardBorders(index, LAYOUTS.sm);
      const lgBorders = getCardBorders(index, LAYOUTS.lg);

      return (
        <Paper
          key={card.key}
          elevation={0}
          square
          data-testid={`perq-summary-card-${card.key}`}
          onClick={() => onCardClick(card.key)}
          role={isClickable ? "button" : undefined}
          tabIndex={isClickable ? 0 : undefined}
          onKeyDown={
            isClickable
              ? (event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    onCardClick(card.key);
                  }
                }
              : undefined
          }
          sx={{
            minHeight: 73,
            padding: isMobile ? "12px" : "5px 20px",
            backgroundColor: isSelected ? "#fff8f6" : "white",
            boxShadow: isSelected ? "inset 0 0 0 1px #ff653a" : "none",
            transition: "background-color 0.15s ease, box-shadow 0.15s ease",
            ...smBorders,
            "@media (min-width: 1000px)": {
              ...lgBorders,
            },
            cursor: isClickable ? "pointer" : "default",
            "&:hover":
              isClickable && !isSelected
                ? {
                    backgroundColor: "#fafcff",
                    boxShadow: "inset 0 0 0 1px #d7e3ff",
                  }
                : undefined,
          }}
        >
          <CardContainer isMobile={isMobile}>
            <IconContainer>{card.icon}</IconContainer>
            <InformationContainer isMobile={isMobile}>
              <TitleContainer>
                <StatisticsTitle data-testid={`perq-summary-title-${card.key}`}>
                  {card.title}
                </StatisticsTitle>
                {card.receiptRow ? (
                  <ReceiptTooltip
                    placement="bottom-start"
                    describeChild
                    title={
                      <SummaryReceipt
                        title={card.title}
                        highlightRow={card.receiptRow}
                        data={data}
                        unavailable={loading || error}
                      />
                    }
                  >
                    <StyledInfoIcon
                      data-testid={`perq-summary-info-${card.key}`}
                      aria-label={`${card.title}: ${receiptDescription(
                        card.receiptRow,
                        data,
                        loading || error,
                      )}`}
                    />
                  </ReceiptTooltip>
                ) : (
                  <LightTooltip title={card.description} placement="top">
                    <StyledInfoIcon data-testid={`perq-summary-info-${card.key}`} />
                  </LightTooltip>
                )}
              </TitleContainer>
              {loading ? (
                <StatisticSkeletalLoader />
              ) : error ? (
                <StatisticsValue data-testid={`perq-summary-value-${card.key}`}>
                  {NEUTRAL_VALUE}
                </StatisticsValue>
              ) : (
                <React.Fragment>
                  <StatisticsValue data-testid={`perq-summary-value-${card.key}`}>
                    {card.getValue(data)}
                  </StatisticsValue>
                  {subValue ? (
                    <Typography
                      data-testid={`perq-summary-subvalue-${card.key}`}
                      sx={{ fontSize: "12px", color: "#a1a1aa", mt: "2px" }}
                    >
                      {subValue}
                    </Typography>
                  ) : null}
                </React.Fragment>
              )}
            </InformationContainer>
          </CardContainer>
        </Paper>
      );
    })}
  </Box>
);
